import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  File,
  Image as ImageIcon,
  FileSpreadsheet,
  FileText,
  Trash2,
  Download,
  Eye,
  X,
  Camera,
  Upload,
  Loader2,
  ExternalLink,
  AlertTriangle,
  Maximize2,
  Minimize2,
  Video,
} from "lucide-react";
import { toast } from "sonner";
import { Timestamp } from "firebase/firestore";
import { uploadProductFile, deleteProductFile } from "@/services/productNotesService";
import type { ProductFile } from "@/types/productNotes";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useAppConfig } from "@/hooks/useAppConfig";

// Product reference files need broader type support than general uploads
// (videos, spreadsheets, CSVs in addition to images/PDFs/docs).
const PRODUCT_ACCEPTED_TYPES = ["image/*", "video/*", ".pdf", ".xls", ".xlsx", ".csv", ".doc", ".docx"];

interface ProductFilesSectionProps {
  files: ProductFile[];
  onFilesChange: (files: ProductFile[]) => void;
  disabled?: boolean;
  userId?: string;
  isSystem?: boolean;
  bank?: string;
  loanType?: string;
}

interface PendingItem {
  id: string;
  file: File;
  previewUrl: string | null; // blob URL for images; null for other types
}

const getFileIcon = (type: string) => {
  if (type.startsWith("image/")) return ImageIcon;
  if (type.startsWith("video/")) return Video;
  if (type.includes("spreadsheet") || type.includes("excel") || type.includes("csv")) return FileSpreadsheet;
  if (type.includes("pdf")) return FileText;
  if (type.includes("word") || type.includes("document")) return FileText;
  return File;
};

const getFileCategory = (type: string): "image" | "video" | "pdf" | "doc" | "other" => {
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";
  if (type.includes("pdf")) return "pdf";
  if (type.includes("word") || type.includes("document") || type.includes("spreadsheet") || type.includes("excel")) return "doc";
  return "other";
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function ProductFilesSection({
  files,
  onFilesChange,
  disabled = false,
  userId = "unknown",
  isSystem = false,
  bank = "",
  loanType = "",
}: ProductFilesSectionProps) {
  const { config } = useAppConfig();
  const [uploading, setUploading] = useState(false);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [viewingFile, setViewingFile] = useState<ProductFile | null>(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Keep refs to the latest values so async callbacks always read current data
  const filesRef = useRef(files);
  useEffect(() => { filesRef.current = files; }, [files]);
  const configRef = useRef(config);
  useEffect(() => { configRef.current = config; }, [config]);
  const pendingItemsRef = useRef(pendingItems);
  useEffect(() => { pendingItemsRef.current = pendingItems; }, [pendingItems]);

  // Core upload: immediately shows pending cards, then uploads each file to Firebase.
  const uploadFiles = useCallback(async (filesToUpload: File[]) => {
    if (!bank || !loanType || disabled) return;

    const maxSizeBytes = configRef.current.maxFileUploadSizeMB * 1024 * 1024;
    const valid = filesToUpload.filter(f => {
      if (f.size > maxSizeBytes) {
        toast.error(`${f.name} exceeds the ${configRef.current.maxFileUploadSizeMB} MB size limit`);
        return false;
      }
      return true;
    });
    if (!valid.length) return;

    if (configRef.current.maxFileUploads &&
        filesRef.current.length + pendingItemsRef.current.length + valid.length > configRef.current.maxFileUploads) {
      toast.error(`Maximum ${configRef.current.maxFileUploads} files allowed`);
      return;
    }

    // Create local preview cards immediately — before any async work
    const newItems: PendingItem[] = valid.map(f => ({
      id: crypto.randomUUID(),
      file: f,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    setPendingItems(prev => [...prev, ...newItems]);

    setUploading(true);
    const uploaded: ProductFile[] = [];
    for (const item of newItems) {
      try {
        const productFile = await uploadProductFile(item.file, isSystem, userId, bank, loanType);
        uploaded.push(productFile);
      } catch (error) {
        console.error(`Error uploading ${item.file.name}:`, error);
        toast.error(`Failed to upload ${item.file.name}`);
      } finally {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        setPendingItems(prev => prev.filter(p => p.id !== item.id));
      }
    }
    if (uploaded.length > 0) {
      onFilesChange([...filesRef.current, ...uploaded]);
      toast.success(`${uploaded.length} file${uploaded.length !== 1 ? 's' : ''} uploaded successfully`);
    }
    setUploading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bank, loanType, disabled, isSystem, userId, onFilesChange]);

  const handleFileButtonClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = PRODUCT_ACCEPTED_TYPES.join(',');
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (!target.files?.length) return;
      uploadFiles(Array.from(target.files));
    };
    input.click();
  }, [uploadFiles]);

  const handleCameraButtonClick = useCallback(async () => {
    const { capturePhoto } = await import('@/services/cameraService');
    try {
      const photoFile = await capturePhoto(90);
      if (photoFile) uploadFiles([photoFile]);
    } catch (error) {
      console.error('Camera capture error:', error);
      toast.error('Failed to capture photo');
    }
  }, [uploadFiles]);

  // Reset viewer state when file changes
  useEffect(() => {
    setIframeError(false);
    setIframeLoading(true);
  }, [viewingFile?.id]);

  const handleDelete = async (fileId: string) => {
    if (disabled) return;

    const file = files.find((f) => f.id === fileId);
    if (file) {
      try {
        await deleteProductFile(file.url);
        onFilesChange(files.filter((f) => f.id !== fileId));
        toast.success("File deleted successfully");
        if (viewingFile?.id === fileId) setViewingFile(null);
      } catch (error) {
        console.error("Error deleting file:", error);
        toast.error("Failed to delete file");
      }
    }
  };

  const handleDownload = (file: ProductFile) => {
    try {
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading:", error);
    }
  };

  // ---- VIEWER CONTENT RENDERER ----
  const renderViewerContent = (file: ProductFile) => {
    const category = getFileCategory(file.type);

    switch (category) {
      case "video":
        return (
          <div className="w-full bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              controls
              autoPlay
              playsInline
              className="w-full rounded-lg"
              style={{ maxHeight: "70vh" }}
            >
              <source src={file.url} type={file.type} />
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case "image":
        return (
          <div className="w-full flex items-center justify-center bg-black/5 rounded-lg overflow-hidden relative">
            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={4}
              centerOnInit
            >
              {({ zoomIn, zoomOut }) => (
                <>
                  <div className="absolute top-2 right-2 z-10 flex gap-1">
                    <Button variant="secondary" size="icon" className="h-8 w-8" onClick={() => zoomIn()}>
                      <Maximize2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="secondary" size="icon" className="h-8 w-8" onClick={() => zoomOut()}>
                      <Minimize2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <TransformComponent
                    wrapperStyle={{ width: "100%", maxHeight: "70vh" }}
                    contentStyle={{ width: "100%", display: "flex", justifyContent: "center" }}
                  >
                    <img
                      src={file.url}
                      alt={file.name}
                      className="max-w-full max-h-[70vh] object-contain"
                    />
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
          </div>
        );

      case "pdf":
        return (
          <div className="w-full h-[70vh] rounded-lg overflow-hidden relative">
            {iframeLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading PDF...</p>
                </div>
              </div>
            )}
            <iframe
              src={`${file.url}#toolbar=1&navpanes=1&scrollbar=1`}
              className="w-full h-full border-0"
              title={file.name}
              onLoad={() => setIframeLoading(false)}
              onError={() => { setIframeError(true); setIframeLoading(false); }}
            />
          </div>
        );

      case "doc": {
        const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;
        const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(file.url)}&embedded=true`;

        if (iframeError) {
          return (
            <div className="text-center py-12">
              <AlertTriangle className="h-10 w-10 text-warning mx-auto mb-3" />
              <h4 className="font-medium mb-2">Unable to preview document</h4>
              <p className="text-sm text-muted-foreground mb-4">
                The document could not be rendered in-app. Try opening it externally.
              </p>
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center justify-center gap-2">
                  <Button onClick={() => window.open(officeViewerUrl, "_blank")}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in Office
                  </Button>
                  <Button variant="outline" onClick={() => window.open(googleViewerUrl, "_blank")}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in Google Docs
                  </Button>
                </div>
                <Button variant="secondary" onClick={() => window.open(file.url, "_blank")}>
                  Download File
                </Button>
              </div>
            </div>
          );
        }

        return (
          <div className="w-full h-[70vh] rounded-lg overflow-hidden relative">
            {iframeLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading document...</p>
                  <p className="text-xs text-muted-foreground mt-1">If loading takes too long, try downloading the file.</p>
                </div>
              </div>
            )}
            <iframe
              src={officeViewerUrl}
              className="w-full h-full border-0"
              title={file.name}
              onLoad={() => setIframeLoading(false)}
              onError={() => { setIframeError(true); setIframeLoading(false); }}
            />
          </div>
        );
      }

      default:
        return (
          <div className="text-center py-12">
            <File className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <h4 className="font-medium mb-2">Preview not available</h4>
            <p className="text-sm text-muted-foreground mb-4">
              This file type cannot be previewed in the browser.
            </p>
            <Button onClick={() => window.open(file.url, "_blank")}>
              <ExternalLink className="h-4 w-4 mr-2" />
              View in New Tab
            </Button>
          </div>
        );
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Reference Files</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Uploaded Files List */}
          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((file) => {
                const Icon = getFileIcon(file.type);
                const uploadDate =
                  file.uploadedAt instanceof Timestamp
                    ? file.uploadedAt.toDate()
                    : file.uploadedAt;
                return (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 group"
                  >
                    <div
                      className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
                      onClick={() => setViewingFile(file)}
                    >
                      <div className="h-10 w-10 rounded bg-background flex items-center justify-center shrink-0">
                        {file.type.startsWith("image/") ? (
                          <img
                            src={file.url}
                            alt={file.name}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <Icon className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(file.size)} &middot;{" "}
                          {uploadDate.toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setViewingFile(file)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleDownload(file)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {!disabled && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(file.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pending files — locally staged, currently uploading */}
          {pendingItems.map((item) => {
            const Icon = getFileIcon(item.file.type);
            return (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 opacity-70"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="h-10 w-10 rounded bg-background flex items-center justify-center shrink-0 relative">
                    {item.previewUrl ? (
                      <img
                        src={item.previewUrl}
                        alt={item.file.name}
                        className="h-10 w-10 rounded object-cover"
                      />
                    ) : (
                      <Icon className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center rounded bg-black/40">
                      <Loader2 className="h-4 w-4 animate-spin text-white" />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(item.file.size)} &middot; Uploading&hellip;
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

          {/* No files placeholder */}
          {files.length === 0 && pendingItems.length === 0 && (
            <div className="text-center py-4 text-muted-foreground">
              <File className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {disabled
                  ? "No reference files available"
                  : "No files yet. Use the options below to upload."}
              </p>
            </div>
          )}

          {/* Inline Upload Box */}
          {!disabled && (
            <div className="border-2 border-dashed border-border rounded-xl p-4">
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full justify-start rounded-xl"
                  onClick={handleCameraButtonClick}
                  disabled={!bank || !loanType}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Take Photo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full justify-start rounded-xl"
                  onClick={handleFileButtonClick}
                  disabled={!bank || !loanType}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload File
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3 text-center">
                {(!bank || !loanType)
                  ? "Select a lender and loan type to upload files"
                  : `Images, Videos, PDFs, Docs, Excel • Max ${config.maxFileUploads} files, ${config.maxFileUploadSizeMB}MB each`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rich File Viewer Dialog */}
      <Dialog
        open={viewingFile !== null}
        onOpenChange={(open) => { if (!open) setViewingFile(null); }}
      >
        <DialogContent className="max-w-5xl w-[calc(100vw-1rem)] max-h-[95dvh] p-0 gap-0 overflow-hidden [&>.absolute]:hidden">
          {viewingFile && (
            <>
              {/* Viewer Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b bg-card/95 backdrop-blur-sm">
                <div className="flex-1 min-w-0 mr-3">
                  <div className="flex items-center gap-2 mb-0.5">
                    {(() => {
                      const Icon = getFileIcon(viewingFile.type);
                      return <Icon className="h-4 w-4 text-muted-foreground shrink-0" />;
                    })()}
                    <h3 className="font-semibold text-sm sm:text-base truncate">
                      {viewingFile.name}
                    </h3>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(viewingFile.size)}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-8"
                    onClick={() => handleDownload(viewingFile)}
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    <span className="hidden sm:inline">Download</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setViewingFile(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Viewer Content */}
              <div className="p-4 overflow-y-auto max-h-[calc(95dvh-60px)]">
                {renderViewerContent(viewingFile)}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
